import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Video } from '@/components/ui/video'
import type { IntroVideoSource } from '@/lib/audition/intro-video'

/**
 * Collapsible intro video for the top of the /apply wizard. The mux branch
 * reuses the courses player; the youtube branch is a placeholder-only
 * privacy-enhanced embed (courses' Video stays Mux-only by design).
 * Collapsing unmounts the player so playback stops.
 *
 * Autoplays muted — browsers only honor autoplay without a user gesture when
 * the video starts muted, so viewers unmute in the player controls. A
 * returning applicant gets `defaultCollapsed`, which also means no autoplay:
 * the video stays available to re-watch without ambushing them with sound.
 */
export function IntroVideo({
  source,
  defaultCollapsed = false,
}: {
  source: IntroVideoSource
  defaultCollapsed?: boolean
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  // The wizard derives `defaultCollapsed` from async tRPC queries that
  // haven't resolved yet on first render, so the `useState` initial value
  // above is often stale (false) for a returning applicant. Re-collapse
  // when the prop later flips to true. This intentionally does NOT fire on
  // every render — `defaultCollapsed` only transitions false->true once the
  // query resolves, so a user who then clicks "Show video" (setCollapsed
  // false) is not fought by this effect re-collapsing them, since the prop
  // never flips back to false->true again.
  useEffect(() => {
    if (defaultCollapsed) setCollapsed(true)
  }, [defaultCollapsed])

  // Collapsed: the player is unmounted, so a single centered "Show video"
  // button is the only control — and the only way a returning (auto-
  // collapsed) applicant gets the video back.
  if (collapsed) {
    return (
      <section className="flex justify-center">
        <Button
          type="button"
          variant="outline"
          onClick={() => setCollapsed(false)}
        >
          Show video
        </Button>
      </section>
    )
  }

  return (
    <section className="space-y-1">
      {source.provider === 'mux' ? (
        <Video
          playbackId={source.playbackId}
          className="my-0"
          autoPlay="muted"
        />
      ) : (
        <div
          className="overflow-hidden rounded-md"
          style={{ aspectRatio: 16 / 9 }}
        >
          <iframe
            className="h-full w-full border-0"
            src={`https://www.youtube-nocookie.com/embed/${source.id}?autoplay=1&mute=1`}
            title="Introduction video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
      {/* Hide control sits BELOW the player, right-aligned. */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(true)}
        >
          Hide video
        </Button>
      </div>
    </section>
  )
}
